import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Monitor, Smartphone, Loader2, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function deviceLabel(ua = "") {
  const u = ua.toLowerCase();
  const isMobile = /iphone|android|mobile|ipad/.test(u);
  let browser = "Browser";
  if (u.includes("edg")) browser = "Edge";
  else if (u.includes("chrome")) browser = "Chrome";
  else if (u.includes("firefox")) browser = "Firefox";
  else if (u.includes("safari")) browser = "Safari";
  let os = "";
  if (u.includes("iphone") || u.includes("ipad")) os = "iOS";
  else if (u.includes("android")) os = "Android";
  else if (u.includes("mac")) os = "macOS";
  else if (u.includes("windows")) os = "Windows";
  else if (u.includes("linux")) os = "Linux";
  return { text: [browser, os].filter(Boolean).join(" · ") || "Unknown device", isMobile };
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function SessionsPanel() {
  const qc = useQueryClient();
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["my-sessions"],
    queryFn: () => api.get("/me/sessions").then((r) => r.data),
  });

  const revokeOne = useMutation({
    mutationFn: (id) => api.delete(`/me/sessions/${id}`),
    onSuccess: () => { toast.success("Device signed out"); qc.invalidateQueries({ queryKey: ["my-sessions"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const revokeOthers = useMutation({
    mutationFn: () => api.post("/me/sessions/revoke-others"),
    onSuccess: (r) => { toast.success(`Signed out ${r.data.revoked} other device${r.data.revoked === 1 ? "" : "s"}`); qc.invalidateQueries({ queryKey: ["my-sessions"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const others = sessions.filter((s) => !s.current);

  return (
    <div className="eyw-card p-8 mt-8" data-testid="sessions-panel">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ShieldCheck size={20} className="text-[#5C8A72]" /> Devices &amp; sessions</h2>
        {others.length > 0 && (
          <button onClick={() => revokeOthers.mutate()} disabled={revokeOthers.isPending} data-testid="revoke-others-btn"
            className="eyw-btn-ghost !py-2 text-sm text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50">
            {revokeOthers.isPending ? <Loader2 className="animate-spin" size={15} /> : <LogOut size={15} />} Sign out all other devices
          </button>
        )}
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">These are the devices currently signed in to your account. Sign out any you don't recognize.</p>
      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#144F75]" /></div>
      ) : (
        <ul className="space-y-3">
          {sessions.map((s) => {
            const d = deviceLabel(s.user_agent);
            const Icon = d.isMobile ? Smartphone : Monitor;
            return (
              <li key={s.id} className="flex items-center justify-between gap-3 border-b border-[#E5E0D8] pb-3 last:border-0" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Icon size={20} className="text-[#144F75] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2">
                      {d.text}
                      {s.current && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]" data-testid={`session-current-${s.id}`}>This device</span>}
                    </div>
                    <div className="text-xs text-[#8FA89B] truncate">{s.ip ? `${s.ip} · ` : ""}active {timeAgo(s.last_seen)}</div>
                  </div>
                </div>
                {!s.current && (
                  <button onClick={() => revokeOne.mutate(s.id)} disabled={revokeOne.isPending} data-testid={`session-revoke-${s.id}`}
                    className="text-xs text-red-500 hover:text-red-600 shrink-0">Sign out</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
